
import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Plus, Trash2, ListFilter, ArrowDownCircle, ArrowUpCircle, Search, FileUp, Sparkles, X, Check, Loader2, Calendar, AlertCircle, Info, CalendarClock, ChevronLeft, ChevronRight, RotateCcw, Repeat, Square, CheckSquare } from 'lucide-react';
import { Transaction, RecurringFrequency } from '../../types';

interface Props {
  transactions: Transaction[];
  categories: any[];
  onAdd: (name: string, amount: number, type: 'income' | 'expense', category: string, date: string, isRecurring?: boolean, frequency?: RecurringFrequency) => void;
  onBulkAdd: (txs: Transaction[]) => void;
  onDelete: (id: string) => void;
  onBulkDelete: (ids: string[]) => void;
  onBulkUpdateRecurring: (ids: string[], isRecurring: boolean, frequency?: RecurringFrequency) => void;
  selectedMonth: string;
  onMonthChange: (month: string) => void;
  processCSV: (text: string) => Transaction[];
  importStatus: {message: string, type: 'error' | 'success' | 'info'} | null;
  onClearStatus: () => void;
}

export const TransactionLedger: React.FC<Props> = ({ 
  transactions, 
  categories, 
  onAdd, 
  onBulkAdd, 
  onDelete, 
  onBulkDelete,
  onBulkUpdateRecurring,
  selectedMonth,
  onMonthChange,
  processCSV,
  importStatus,
  onClearStatus
}) => {
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  
  // Default date should be the first of the selected month, or today if viewing current month
  const getInitialDate = () => {
    const today = new Date().toISOString().split('T')[0];
    if (today.startsWith(selectedMonth)) return today;
    return `${selectedMonth}-01`;
  };

  const [dateInput, setDateInput] = useState(getInitialDate());
  const isDateValid = useMemo(() => {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateInput)) return false;
    const d = new Date(dateInput);
    return !isNaN(d.getTime());
  }, [dateInput]);

  const [type, setType] = useState<'income' | 'expense'>('expense');
  const [category, setCategory] = useState(categories[0]?.label || 'Misc');
  const [showAllTime, setShowAllTime] = useState(false);
  
  // Picker visibility states
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isFeedPickerOpen, setIsFeedPickerOpen] = useState(false);
  
  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Recurring state
  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState<RecurringFrequency>('monthly');

  // Refs for click-outside detection
  const datePickerRef = useRef<HTMLDivElement>(null);
  const feedPickerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [stagedTransactions, setStagedTransactions] = useState<Transaction[]>([]);

  const todayMonth = useMemo(() => new Date().toISOString().slice(0, 7), []);

  // Sync date input when month changes to keep it relevant to the view
  useEffect(() => {
    setDateInput(getInitialDate());
  }, [selectedMonth]);

  // Handle click outside to close pickers
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setIsDatePickerOpen(false);
      }
      if (feedPickerRef.current && !feedPickerRef.current.contains(event.target as Node)) {
        setIsFeedPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !amount || !isDateValid) return;
    onAdd(name, parseFloat(amount), type, category, dateInput, isRecurring, isRecurring ? frequency : undefined);
    
    // Clear form but keep the relevant date if user is batch entering
    setName(''); 
    setAmount('');
    setIsRecurring(false);
    setIsDatePickerOpen(false);
  };

  const filteredTransactions = useMemo(() => {
    return showAllTime 
      ? transactions 
      : transactions.filter(t => t.date.startsWith(selectedMonth));
  }, [transactions, selectedMonth, showAllTime]);

  const groupedTransactions = useMemo((): Record<string, Transaction[]> => {
    const sorted = [...filteredTransactions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    const groups: Record<string, Transaction[]> = {};
    
    sorted.forEach(tx => {
      const txDate = tx.date || new Date().toISOString().split('T')[0];
      if (!groups[txDate]) groups[txDate] = [];
      groups[txDate].push(tx);
    });
    
    return groups;
  }, [filteredTransactions]);

  const formattedMonthDisplay = useMemo(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    return new Date(year, month - 1, 15).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
  }, [selectedMonth]);

  const changeMonth = (dir: 'prev' | 'next') => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const d = new Date(year, month - 1, 15);
    if (dir === 'prev') d.setMonth(d.getMonth() - 1);
    else d.setMonth(d.getMonth() + 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    onMonthChange(`${y}-${m}`);
    setSelectedIds(new Set());
    setIsFeedPickerOpen(false);
  };

  const toggleSelection = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredTransactions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredTransactions.map(t => t.id)));
    }
  };

  const handleBulkDelete = () => {
    if (confirm(`Delete ${selectedIds.size} selected transactions?`)) {
      onBulkDelete(Array.from(selectedIds));
      setSelectedIds(new Set());
    }
  };

  const handleBulkMarkRecurring = () => {
    onBulkUpdateRecurring(Array.from(selectedIds), true, 'monthly');
    setSelectedIds(new Set());
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    onClearStatus();
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      if (text) {
        const parsed = processCSV(text);
        setStagedTransactions(parsed);
      }
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileClick = () => {
    onClearStatus();
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-8 animate-fade-in relative pb-24">
      {importStatus && (
        <div className={`p-4 rounded-2xl flex items-center gap-3 animate-fade-in border ${
          importStatus.type === 'error' ? 'bg-rose-50 border-rose-100 text-rose-600' : 
          importStatus.type === 'success' ? 'bg-emerald-50 border-emerald-100 text-emerald-600' : 
          'bg-sky-50 border-sky-100 text-sky-600'
        }`}>
          {importStatus.type === 'error' ? <AlertCircle size={20} /> : <Info size={20} />}
          <p className="text-xs font-bold uppercase tracking-tight">{importStatus.message}</p>
          
          {stagedTransactions.length > 0 && importStatus.type === 'success' && (
            <button 
              onClick={() => {
                onBulkAdd(stagedTransactions);
                setStagedTransactions([]);
              }}
              className="ml-4 px-3 py-1 bg-emerald-600 text-white text-[9px] font-black uppercase tracking-widest rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-1"
            >
              <Check size={12} /> Confirm Import
            </button>
          )}

          <button onClick={() => { onClearStatus(); setStagedTransactions([]); }} className="ml-auto p-1 rounded-lg hover:bg-black/5"><X size={16} /></button>
        </div>
      )}

      {/* Bulk Action Toolbar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 w-full max-w-xl z-[150] px-6 animate-slide-up">
          <div className="bg-navy rounded-3xl p-4 shadow-2xl border border-white/10 flex items-center justify-between text-white">
            <div className="flex items-center gap-4 pl-2">
              <span className="text-[10px] font-inter font-black uppercase tracking-widest text-emerald">{selectedIds.size} Selected</span>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={handleBulkMarkRecurring}
                className="flex items-center gap-2 px-4 py-2 bg-emerald hover:bg-white hover:text-navy text-[10px] font-inter font-black uppercase tracking-widest rounded-xl transition-all"
              >
                <Repeat size={14} /> Make Recurring
              </button>
              <button 
                onClick={handleBulkDelete}
                className="flex items-center gap-2 px-4 py-2 bg-rose-500/20 hover:bg-rose-500 text-rose-500 hover:text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all"
              >
                <Trash2 size={14} /> Delete
              </button>
              <button 
                onClick={() => setSelectedIds(new Set())}
                className="p-2 text-white/40 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="fin-card p-8 rounded-4xl shadow-xl shadow-navy/5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div>
            <h2 className="text-2xl font-extrabold text-navy tracking-tight">Financial Ledger</h2>
            <p className="text-xs font-inter text-slate-400 uppercase font-bold tracking-widest">Atomic Entry System</p>
          </div>
          <div className="flex bg-slate-50 p-1.5 rounded-2xl">
            <button 
              onClick={() => setType('expense')}
              className={`flex items-center gap-2 px-5 py-2.5 text-xs font-extrabold rounded-xl transition-all ${type === 'expense' ? 'bg-white text-navy shadow-sm' : 'text-slate-400 hover:text-navy'}`}
            >
              <ArrowDownCircle size={16} /> Expense
            </button>
            <button 
              onClick={() => setType('income')}
              className={`flex items-center gap-2 px-5 py-2.5 text-xs font-extrabold rounded-xl transition-all ${type === 'income' ? 'bg-white text-navy shadow-sm' : 'text-slate-400 hover:text-navy'}`}
            >
              <ArrowUpCircle size={16} /> Income
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            <div className="md:col-span-4">
              <input type="text" placeholder="Description" value={name} onChange={e => setName(e.target.value)} className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl text-base text-navy font-semibold focus:bg-white focus:border-electric outline-none transition-all" />
            </div>
            <div className="md:col-span-2 relative" ref={datePickerRef}>
              <div className="relative group">
                <button 
                  type="button"
                  onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
                  className={`w-full bg-slate-50 border p-4 rounded-2xl text-base font-semibold flex items-center justify-between transition-all active:scale-[0.98] ${isDatePickerOpen ? 'border-emerald bg-white ring-2 ring-emerald/5 text-emerald' : 'border-slate-100 text-navy hover:bg-slate-100'} ${!isDateValid ? 'border-rose-200' : ''}`}
                >
                  <span className="flex items-center gap-2">
                    {isDateValid ? new Date(dateInput).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' }) : 'Invalid Date'}
                    {!isDateValid ? <AlertCircle size={14} className="text-rose-500" /> : <Check size={14} className="text-emerald-500" />}
                  </span>
                  <CalendarClock size={16} className={isDatePickerOpen ? 'text-emerald' : 'text-slate-400'} />
                </button>
              </div>
              {isDatePickerOpen && (
                <div className="absolute top-16 left-0 bg-white border border-slate-100 rounded-2xl shadow-2xl p-4 z-[100] w-64 animate-fade-in space-y-3">
                  <div>
                    <label className="block text-[9px] font-inter font-black text-slate-400 uppercase tracking-widest mb-1 px-1">Manual Entry (YYYY-MM-DD)</label>
                    <input 
                      type="text" 
                      placeholder="2024-01-01"
                      value={dateInput} 
                      onChange={e => setDateInput(e.target.value)}
                      className={`w-full p-3 text-base font-bold text-navy bg-slate-50 rounded-xl outline-none border transition-all ${isDateValid ? 'border-slate-100 focus:border-emerald' : 'border-rose-200 bg-rose-50 focus:border-rose-500'}`}
                    />
                  </div>
                  <div className="h-px bg-slate-100 mx-2"></div>
                  <div>
                    <label className="block text-[9px] font-inter font-black text-slate-400 uppercase tracking-widest mb-1 px-1">Visual Picker</label>
                    <input 
                      type="date" 
                      value={isDateValid ? dateInput : ''} 
                      onChange={e => { setDateInput(e.target.value); setIsDatePickerOpen(false); }}
                      className="w-full p-2 text-base font-bold text-navy bg-slate-50 rounded-lg outline-none border border-slate-100 focus:border-emerald"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="md:col-span-2">
              <select value={category} onChange={e => setCategory(e.target.value)} className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl text-base text-navy font-semibold focus:bg-white focus:border-electric outline-none active:scale-[0.98] transition-all">
                {type === 'expense' ? categories.map((c: any) => <option key={c.label} value={c.label}>{c.label}</option>) : <option value="Income">Income Pool</option>}
              </select>
            </div>
            <div className="md:col-span-3 relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-navy font-bold text-base">R</span>
              <input type="number" step="0.01" placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} className="w-full bg-slate-50 border border-slate-100 p-4 pl-9 rounded-2xl text-base text-navy font-bold focus:bg-white focus:border-electric outline-none transition-all" />
            </div>
            <div className="md:col-span-1">
              <button type="submit" disabled={!name || !amount || !isDateValid} className="w-full h-full flex items-center justify-center bg-emerald hover:bg-navy disabled:opacity-30 text-white rounded-2xl transition-all shadow-lg shadow-emerald/20">
                <Plus size={24} />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3 px-1">
             <button 
               type="button"
               onClick={() => setIsRecurring(!isRecurring)}
               className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-inter font-black uppercase tracking-widest transition-all ${isRecurring ? 'bg-emerald text-white shadow-lg' : 'bg-slate-50 text-slate-400 hover:text-navy'}`}
             >
               <Repeat size={14} />
               {isRecurring ? 'Recurring Active' : 'Mark as Recurring'}
             </button>

             {isRecurring && (
               <div className="flex items-center gap-2 animate-fade-in">
                 <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Every</span>
                 <select 
                   value={frequency} 
                   onChange={(e) => setFrequency(e.target.value as RecurringFrequency)}
                   className="bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-lg text-[10px] font-black text-navy uppercase tracking-widest outline-none focus:border-electric"
                 >
                   <option value="daily">Day</option>
                   <option value="weekly">Week</option>
                   <option value="monthly">Month</option>
                   <option value="yearly">Year</option>
                 </select>
               </div>
             )}
          </div>
        </form>
      </div>

      <div className="space-y-4">
        <div className="bg-white border border-slate-100 rounded-3xl p-5 flex flex-col md:flex-row justify-between items-center gap-4 shadow-sm relative z-[70]">
          <div className="flex items-center gap-4">
            <button 
              onClick={toggleSelectAll}
              className={`p-1.5 rounded-lg transition-colors ${selectedIds.size === filteredTransactions.length && filteredTransactions.length > 0 ? 'bg-emerald/10 text-emerald' : 'text-slate-300 hover:text-slate-400'}`}
              title="Select all visible"
            >
              {selectedIds.size === filteredTransactions.length && filteredTransactions.length > 0 ? <CheckSquare size={18} /> : <Square size={18} />}
            </button>
            <h3 className="text-[10px] font-inter font-extrabold text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <ListFilter size={14} /> Activity Feed
            </h3>
            <div className="h-4 w-px bg-slate-100"></div>
            <div className="flex items-center gap-2 relative" ref={feedPickerRef}>
              <button onClick={() => changeMonth('prev')} className="p-1.5 hover:bg-slate-50 rounded-lg text-slate-400 hover:text-navy transition-all"><ChevronLeft size={16} /></button>
              <button 
                onClick={() => setIsFeedPickerOpen(!isFeedPickerOpen)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-inter font-black uppercase tracking-widest transition-all ${isFeedPickerOpen ? 'bg-emerald text-white shadow-lg' : 'bg-slate-50 hover:bg-slate-100 text-navy'}`}
              >
                <Calendar size={14} className={isFeedPickerOpen ? 'text-white' : 'text-emerald'} />
                {formattedMonthDisplay}
              </button>
              <button onClick={() => changeMonth('next')} className="p-1.5 hover:bg-slate-50 rounded-lg text-slate-400 hover:text-navy transition-all"><ChevronRight size={16} /></button>
              
              {isFeedPickerOpen && (
                <div className="absolute top-12 left-0 w-72 bg-navy rounded-3xl shadow-2xl p-6 text-white border border-white/10 animate-fade-in z-[110]">
                  <div className="flex justify-between items-center mb-6 px-1">
                    <button onClick={() => changeMonth('prev')} className="p-1 hover:bg-white/10 rounded-lg"><ChevronLeft size={16} /></button>
                    <span className="font-bold text-sm">{selectedMonth.split('-')[0]}</span>
                    <button onClick={() => changeMonth('next')} className="p-1 hover:bg-white/10 rounded-lg"><ChevronRight size={16} /></button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, idx) => {
                      const monthStr = String(idx + 1).padStart(2, '0');
                      const isSelected = selectedMonth.split('-')[1] === monthStr;
                      return (
                        <button
                          key={m}
                          onClick={() => {
                            const year = selectedMonth.split('-')[0];
                            onMonthChange(`${year}-${monthStr}`);
                            setIsFeedPickerOpen(false);
                            setSelectedIds(new Set());
                          }}
                          className={`py-2 rounded-xl text-[10px] font-black uppercase transition-all ${isSelected ? 'bg-electric text-white' : 'hover:bg-white/5 text-slate-400'}`}
                        >
                          {m}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedMonth !== todayMonth && (
                <button onClick={() => onMonthChange(todayMonth)} className="flex items-center gap-1.5 px-3 py-1.5 bg-electric/10 text-electric text-[9px] font-black uppercase tracking-widest rounded-xl hover:bg-electric hover:text-white transition-all ml-1"><RotateCcw size={12} /> Jump to Current</button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => { setShowAllTime(!showAllTime); setSelectedIds(new Set()); }} className={`px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest border transition-all ${showAllTime ? 'bg-navy text-white border-navy' : 'bg-white text-slate-400 border-slate-100 hover:border-slate-200'}`}>
              {showAllTime ? 'Filtered View' : 'Show All Time'}
            </button>
            <button onClick={handleFileClick} className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 text-navy hover:text-electric rounded-xl text-[9px] font-black uppercase tracking-widest transition-all border border-slate-100"><FileUp size={12} /> Import CSV</button>
            <input type="file" ref={fileInputRef} className="hidden" accept=".csv" onChange={handleFileUpload} />
          </div>
        </div>
        
        {Object.keys(groupedTransactions).length === 0 ? (
          <div className="bg-white border border-slate-100 rounded-4xl text-center py-24 text-slate-300">
             <Search size={48} className="mx-auto opacity-10 mb-4" />
             <p className="text-sm font-bold uppercase tracking-widest">No activity in {formattedMonthDisplay}</p>
             <button onClick={() => onMonthChange(todayMonth)} className="mt-4 text-[10px] font-black uppercase tracking-widest text-electric hover:underline">Reset to current month</button>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedTransactions).map(([date, txs]: [string, Transaction[]]) => (
              <div key={date} className="space-y-3">
                <div className="sticky top-[80px] z-10 py-2 px-4 flex items-center gap-3">
                  <div className="h-[2px] flex-grow bg-slate-100"></div>
                  <span className="text-[10px] font-inter font-black text-emerald uppercase tracking-[0.2em] bg-finBackground px-2">
                    {new Date(date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                  <div className="h-[2px] flex-grow bg-slate-100"></div>
                </div>
                <div className="bg-white border border-slate-100 rounded-3xl overflow-hidden shadow-sm divide-y divide-slate-50">
                  {txs.map((tx: any) => (
                    <div 
                      key={tx.id} 
                      onClick={() => toggleSelection(tx.id)}
                      className={`p-5 flex justify-between items-center transition-colors group cursor-pointer ${selectedIds.has(tx.id) ? 'bg-electric/5' : 'hover:bg-slate-50/50'}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-1.5 rounded-lg transition-colors ${selectedIds.has(tx.id) ? 'text-electric' : 'text-slate-200 group-hover:text-slate-300'}`}>
                          {selectedIds.has(tx.id) ? <CheckSquare size={18} /> : <Square size={18} />}
                        </div>
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${tx.type === 'income' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                          {tx.type === 'income' ? <ArrowUpCircle size={16} /> : <ArrowDownCircle size={16} />}
                        </div>
                        <div>
                          <div className="text-sm font-bold text-navy tracking-tight flex items-center gap-2">
                            {tx.name}
                            {(tx.isRecurring || tx.parentId) && (
                              <Repeat size={10} className="text-electric" />
                            )}
                          </div>
                          <div className="text-[9px] text-slate-400 uppercase font-bold tracking-widest mt-0.5">
                            {tx.category}
                            {tx.isRecurring && ` • Every ${tx.frequency}`}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <span className={`text-base font-bold tracking-tight ${tx.type === 'income' ? 'text-emerald-500' : 'text-navy'}`}>
                          {tx.type === 'income' ? '+' : '-'}R{tx.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                        <button 
                          onClick={(e) => { e.stopPropagation(); onDelete(tx.id); }}
                          className="p-2 text-slate-200 hover:text-rose-500 transition-all"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
